/**
 * Column definitions for the IAM users table.
 *
 * Lives in its own module rather than inside UsersTable.vue's
 * <script setup> block because:
 *   (a) script setup compiles to a default export only; named
 *       exports for constants aren't supported.
 *   (b) UsersListView.vue also needs the column metadata to build
 *       the column-visibility menu.
 *   (c) Pure TS lets us write tests against the column definitions
 *       directly without mounting a Vue component.
 */
import { h, type VNode } from 'vue'
import type { ColumnDef } from '@tanstack/vue-table'
import type { Composer } from 'vue-i18n'
import StatusDot from '@/components/ui/StatusDot.vue'
import Avatar from '@/components/ui/Avatar.vue'
import RowActionsMenu from './components/RowActionsMenu.vue'
import type { UserView } from './api'

/**
 * Visible-by-default column IDs. The audit decided to hide the
 * legacy financial fields (tax/minGuarantee/etc.) by default; admins
 * who need them can toggle via the column-visibility menu.
 */
export const DEFAULT_VISIBLE: readonly string[] = Object.freeze([
  'userName',
  'owner',
  'external',
  'modified',
  'actions',
])

/**
 * Column IDs that may not be hidden by the user. The userName column
 * is the row identity and `actions` is the kebab menu — both must
 * always be present.
 */
export const LOCKED_COLUMNS: readonly string[] = Object.freeze([
  'userName',
  'actions',
])

/**
 * Every column the table can render, keyed by stable ID. The view
 * picks a subset based on the column-visibility state.
 */
export interface RowActionHandlers {
  onEdit: (user: UserView) => void
  onPermissions: (user: UserView) => void
  onResetPassword: (user: UserView) => void
  onDelete: (user: UserView) => void
}

export function buildColumns(
  i18n: Pick<Composer, 't' | 'd'>,
  handlers: RowActionHandlers,
): ColumnDef<UserView, unknown>[] {
  const { t, d } = i18n

  function formatDateOrEmpty(s: string | null | undefined): string {
    if (!s) return ''
    const date = new Date(s)
    if (Number.isNaN(date.getTime())) return s
    return d(date, 'short')
  }

  return [
    {
      id: 'userName',
      accessorKey: 'userName',
      header: () => t('iam.column.userName'),
      cell: ({ row }): VNode =>
        h('div', { class: 'flex items-center gap-2' }, [
          h(Avatar, { name: row.original.userName, size: 'sm' }),
          h(
            'span',
            { class: 'font-medium text-text-1' },
            row.original.userName,
          ),
        ]),
    },
    {
      id: 'owner',
      accessorKey: 'owner',
      header: () => t('iam.column.owner'),
      cell: ({ row }): VNode =>
        h('span', { class: 'text-text-3' }, row.original.owner ?? '—'),
    },
    {
      id: 'external',
      accessorKey: 'external',
      header: () => t('iam.column.type'),
      cell: ({ row }): VNode =>
        h(
          StatusDot,
          { status: row.original.external ? 'inactive' : 'active' },
          () =>
            t(row.original.external ? 'iam.type.external' : 'iam.type.internal'),
        ),
    },
    {
      id: 'created',
      accessorKey: 'created',
      header: () => t('iam.column.created'),
      cell: ({ row }): VNode =>
        h(
          'span',
          { class: 'font-mono text-text-3 text-[11px]' },
          formatDateOrEmpty(row.original.created),
        ),
    },
    {
      id: 'modified',
      accessorKey: 'modified',
      header: () => t('iam.column.modified'),
      cell: ({ row }): VNode =>
        h(
          'span',
          { class: 'font-mono text-text-3 text-[11px]' },
          formatDateOrEmpty(row.original.modified),
        ),
    },
    {
      id: 'tax',
      accessorKey: 'tax',
      header: () => t('iam.column.tax'),
      cell: ({ row }): VNode =>
        h(
          'span',
          { class: 'font-mono text-text-3' },
          row.original.tax === null ? '—' : String(row.original.tax),
        ),
    },
    {
      id: 'minGuarantee',
      accessorKey: 'minGuarantee',
      header: () => t('iam.column.minGuarantee'),
      cell: ({ row }): VNode =>
        h(
          'span',
          { class: 'font-mono text-text-3' },
          row.original.minGuarantee === null
            ? '—'
            : String(row.original.minGuarantee),
        ),
    },
    {
      id: 'withholdingTax',
      accessorKey: 'withholdingTax',
      header: () => t('iam.column.withholdingTax'),
      cell: ({ row }): VNode =>
        h(
          'span',
          { class: 'font-mono text-text-3' },
          row.original.withholdingTax === null
            ? '—'
            : String(row.original.withholdingTax),
        ),
    },
    {
      id: 'detailedReport',
      accessorKey: 'detailedReport',
      header: () => t('iam.column.detailedReport'),
      cell: ({ row }): VNode =>
        h(
          'span',
          { class: 'font-mono text-text-3' },
          row.original.detailedReport === null
            ? '—'
            : String(row.original.detailedReport),
        ),
    },
    {
      id: 'approve',
      accessorKey: 'approve',
      header: () => t('iam.column.approve'),
      cell: ({ row }): VNode =>
        h(
          'span',
          { class: 'text-text-3' },
          row.original.approve === null
            ? '—'
            : row.original.approve
              ? '✓'
              : '✗',
        ),
    },
    {
      id: 'actions',
      header: '',
      size: 48,
      cell: ({ row }): VNode =>
        h(RowActionsMenu, {
          user: row.original,
          onEdit: handlers.onEdit,
          onPermissions: handlers.onPermissions,
          onResetPassword: handlers.onResetPassword,
          onDelete: handlers.onDelete,
        }),
    },
  ]
}

/**
 * Lightweight column metadata used by ColumnVisibilityMenu. Only
 * needs id + labelKey since the menu doesn't render rows.
 */
export const COLUMN_MENU_DEFS: readonly { id: string; labelKey: string }[] =
  Object.freeze([
    { id: 'userName', labelKey: 'iam.column.userName' },
    { id: 'owner', labelKey: 'iam.column.owner' },
    { id: 'external', labelKey: 'iam.column.type' },
    { id: 'created', labelKey: 'iam.column.created' },
    { id: 'modified', labelKey: 'iam.column.modified' },
    { id: 'tax', labelKey: 'iam.column.tax' },
    { id: 'minGuarantee', labelKey: 'iam.column.minGuarantee' },
    { id: 'withholdingTax', labelKey: 'iam.column.withholdingTax' },
    { id: 'detailedReport', labelKey: 'iam.column.detailedReport' },
    { id: 'approve', labelKey: 'iam.column.approve' },
  ])
